import { inject, observer } from 'mobx-react';
import React from 'react';
import { useHistory } from 'react-router';
import { makeStyles } from 'tss-react/mui';

import ArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';

import Hubspot from 'app/main/common/Hubspot';
import HubspotButton from 'app/main/common/HubspotButton';
import useStores from 'app/main/hooks/use-stores';
import Utils from 'utils/Utils';

const plugins = [
    {
        img: Utils.getImage('products/burp'),
        title: `Burp Suite plugin`,
        description: 'Passively search vulnerable servers while surfing the net',
        link: '/plugins#offensive',
        hubspot: Hubspot.LANDING_FEED_PLUGIN_BURP
    },
    {
        img: Utils.getImage('products/splunk'),
        title: `Splunk plugin`,
        description: 'Vulnerability assessment platform and security scanner',
        link: '/plugins#defensive',
        hubspot: Hubspot.LANDING_FEED_PLUGIN_SPLUNK
    },
    {
        img: Utils.getImage('nmap'),
        title: `Nmap  plugin`,
        description: 'Detect vulnerable servers directly from popular security tool',
        link: '/plugins#offensive',
        hubspot: Hubspot.LANDING_FEED_PLUGIN_NMAP
    }
];

const useStyles = makeStyles()({
    root: {
        padding: 100,
        paddingBottom: 150,
        background: '#fafafa',
        '@media (max-width: 960px)': {
            padding: '80px 20px'
        }
    },
    box: {
        maxWidth: 780,
        margin: '120px auto',
        display: 'grid',
        gridTemplateColumns: 'repeat(1, 3fr)',
        rowGap: 40
    },
    header: {
        fontSize: 52,
        fontWeight: 800
    },
    card: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        padding: 40,
        borderRadius: 40,
        background: '#fff',
        boxShadow: '0px 10px 40px rgba(186, 184, 176, 0.2)',
        transition: 'all 0.2s ease-out',
        '&:hover': {
            boxShadow: '0px 10px 40px rgba(186, 184, 176, 0.5)'
        },
        '@media (max-width: 480px)': {
            flexDirection: 'column',
            alignItems: 'flex-end'
        }
    },
    cardText: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        '@media (max-width: 480px)': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            marginBottom: 24
        }
    },
    image: {
        width: 80,
        height: 80,
        marginRight: 24,
        borderRadius: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#FAFAFA',
        '& img': {
            maxWidth: 38,
            height: 38
        },
        '@media (max-width: 480px)': {
            marginBottom: 24
        }
    },
    title: {
        fontSize: 24,
        fontWeight: 600,
        marginBottom: 4
    },
    subTitle: {
        fontSize: 16,
        color: '#888'
    },
    button: {
        fontSize: 18,
        padding: '20px 40px',
        borderRadius: 100,
        background: 'linear-gradient(249.05deg, #FF8B61 7.18%, #D92E7F 172.88%)',
        '@media (max-width: 960px)': {
            width: '100%'
        }
    },
    arrow: {
        width: 48,
        height: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#FAFAFA',
        borderRadius: 100
    },
    buttonBox: {
        '@media (max-width: 960px)': {
            width: '100%'
        }
    }
});

const Plugins = ({ header = 'Plugins' }) => {
    const { classes } = useStyles();
    const history = useHistory();

    const { userStore } = useStores();
    const onClickPlugin = (link, hubspot) => () => {
        Hubspot.send(hubspot, { email: userStore.user.email });
        history.push(link);
    };

    return (
        <Box className={classes.root} id="plugins">
            <Typography className={classes.header} align="center" variant="h2">
                {header}
            </Typography>

            <div className={classes.box}>
                {plugins.map(({ title, description, img, link, hubspot }, index) => (
                    <Box key={index} className={classes.card} onClick={onClickPlugin(link, hubspot)}>
                        <Box className={classes.cardText}>
                            <div className={classes.image}>
                                <img src={img} alt={title} />
                            </div>
                            <div>
                                <Typography className={classes.title} variant="h3">
                                    {title}
                                </Typography>
                                <Typography className={classes.subTitle} variant="subtitle1">
                                    {description}
                                </Typography>
                            </div>
                        </Box>
                        <div className={classes.arrow}>
                            <ArrowRight style={{ color: '#979797' }} />
                        </div>
                    </Box>
                ))}
            </div>

            <Box textAlign="center" className={classes.buttonBox}>
                <HubspotButton
                    hubspot={Hubspot.LANDING_FEED_PLUGIN_ALL}
                    onClick={() => history.push('/plugins')}
                    href="/plugins"
                    target="_blank"
                    color="primary"
                    variant="contained"
                    className={classes.button}
                >
                    See all integrations
                </HubspotButton>
            </Box>
        </Box>
    );
};

export default inject('userStore')(observer(Plugins));
